import { FC, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import styles from "./Workout.module.scss";
import linkIcon from "@/assets/link-icon.png";
import changeIcon from "@/assets/change.png";
import resultsIcon from "@/assets/results-icon.svg";
import { DEVELOP_START_LINK, PROD_START_LINK } from "@/constants/links";
import { tg } from "@/constants/telegram";
import { ActionButton, ActionButtonVariant } from "@/shared/ui";
import { ExerciseResultsModal } from "@/features/exercise-results-modal";
import { ExerciseAlternativesModal } from "@/features/exercise-alternatives-modal";
import { ExerciseResult } from "@/components/Trains/model";
import { Exercise } from "@/components/Trains/model";

type WorkoutProps = {
  id: number;
  title: string;
  image: string;
  describtion: string;
  trainingId: number;
  isDevelop: boolean;
  duration?: number;
  isFixed?: boolean;
  disabled?: boolean;
  documentation?: string;
  muscleGroup?: string;
  isFree?: boolean;
  hasActiveSubscription?: boolean;
  isIncludedInAllowedCount?: boolean;
  results?: ExerciseResult[] | null;
  config?: Exercise["config"];
};

export const Workout: FC<WorkoutProps> = ({
  title,
  image,
  id,
  trainingId,
  isDevelop,
  isFixed,
  disabled,
  documentation,
  muscleGroup,
  isFree,
  hasActiveSubscription = false,
  isIncludedInAllowedCount = false,
  results,
  config,
}) => {
  const navigate = useNavigate();
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isAlternativesModalOpen, setIsAlternativesModalOpen] = useState(false);
  let link: string;

  if (!isFree) {
    link = isDevelop
      ? `${DEVELOP_START_LINK}?start=exercize-${trainingId}-${id}`
      : `${PROD_START_LINK}?start=exercize-${trainingId}-${id}`;
  } else {
    link = isDevelop
      ? `${DEVELOP_START_LINK}?start=exercize-free${trainingId}-${id}`
      : `${PROD_START_LINK}?start=exercize-free${trainingId}-${id}`;
  }

  const changeTrainDate = isDevelop
    ? `${DEVELOP_START_LINK}?start=EditSample`
    : `${PROD_START_LINK}?start=EditSample`;

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();

    tg.HapticFeedback.impactOccurred("medium");

    // Check if user needs subscription
    if (!isFree && !hasActiveSubscription && !isIncludedInAllowedCount) {
      navigate("/subscription");
      return;
    }

    window.location.href = link;

    setTimeout(() => {
      tg.close();
    }, 200);
  };

  const handleResultsClick = () => {
    setIsResultsModalOpen(true);
  };

  const handleReplaceClick = () => {
    setIsAlternativesModalOpen(true);
  };

  const handleExerciseReplaced = () => {
    // Обновляем страницу после замены упражнения
    window.location.reload();
  };

  const isLocked = !isFree && !hasActiveSubscription && !isIncludedInAllowedCount;

  return (
    <div className="my-4">
      <Link
        to={link}
        target="_blank"
        onClick={handleLinkClick}
        className={`${styles.root} ${isLocked ? styles.rootLocked : ""}`}
        style={{
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <h3 className={styles.root__title}>{title}</h3>
        <p className={styles.root__hint}>Нажмите на упражнение, чтобы открыть видео</p>
        <div className={styles.root__content}>
          <div
            className={styles.root__picture}
            style={{ position: "relative" }}
          >
            {!disabled && !isLocked && (
              <>
                {/* Кнопка Play */}
                <div
                  style={{
                    position: "absolute",
                    zIndex: "30",
                    top: "8px",
                    right: "8px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.3)";
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLinkClick(e);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                      marginLeft: "2px", // Смещение для визуального центрирования треугольника
                    }}
                  >
                    <path
                      d="M8.5 6 L8.5 18 L17.5 12 Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="0.8"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(0,0,0,0.2))",
                      }}
                    />
                  </svg>
                </div>
              </>
            )}
            {isLocked && (
              <div className={styles.lockOverlay}>
                <div className={styles.lockIcon}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <path d="M7 11V8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>
                <div className={styles.lockText}>Требуется подписка</div>
              </div>
            )}
            <div
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                zIndex: "10",
                background:
                  "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(116, 116, 116, 0.9) 100%)",
                top: "0",
                left: "0",
              }}
            ></div>
            <img src={image} alt={title} />
          </div>

          <div className={styles.root__text}>{documentation}</div>
          <div className="py-1 mt-2 px-4 bg-slate-200 rounded-[16px]">
            {muscleGroup}
          </div>
        </div>
      </Link>
      
      <div className="flex gap-2 mt-2">
        {results && results.length > 0 && (
          <ActionButton
            label="Результаты"
            icon={resultsIcon}
            onClick={handleResultsClick}
            variant={ActionButtonVariant.Change}
            type="small"
          />
        )}
        {!isFixed && (
          <ActionButton
            label="Заменить"
            icon={changeIcon}
            onClick={handleReplaceClick}
            variant={ActionButtonVariant.Change}
            type="small"
          />
        )}
      </div>

      {/* Config (показываем только если нет результатов) */}
      {(!results || results.length === 0) && config && Array.isArray(config) && config.length > 0 && (
        <div className="mt-2 px-4 py-3 bg-slate-100 rounded-[12px] text-sm">
          {config.map((c) => (
            <div key={c.approach_num} className="flex justify-between py-1">
              <div className="font-medium">Подход {c.approach_num}</div>
              <div>
                {c.repetitions} повт. × {c.weight}
                {c.repetition_margin !== null && c.repetition_margin !== undefined
                  ? ` (±${c.repetition_margin})`
                  : ""}
              </div>
            </div>
          ))}
        </div>
      )}

      <ExerciseResultsModal
        isOpen={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        results={results || []}
        exerciseTitle={title}
      />

      <ExerciseAlternativesModal
        isOpen={isAlternativesModalOpen}
        onClose={() => setIsAlternativesModalOpen(false)}
        exerciseId={id}
        trainingId={trainingId}
        onExerciseReplaced={handleExerciseReplaced}
      />
    </div>
  );
};
